import { useEffect, useRef } from 'react';
import { isBusinessProfileComplete } from '../constants/businessProfileStatusConstants';
import { isCommercialDocumentType } from '../utils/documentTypeRules';

const hasVatNumber = (profile) => {
  const vatNumber = profile?.party_tax_scheme?.company_id;

  return typeof vatNumber === 'string' && vatNumber.trim() !== '';
};

const isEligibleBilledByProfile = (profile, documentTypeCode) => {
  if (!isBusinessProfileComplete(profile)) {
    return false;
  }

  if (isCommercialDocumentType(documentTypeCode) && !hasVatNumber(profile)) {
    return false;
  }

  return true;
};

export function useAutoPopulateForm({
  profiles,
  customers,
  suppliers,
  documentType,
  setValue,
  getValues,
  invoiceTypeCode,
}) {
  const populated = useRef({
    billedBy: false,
    billedTo: false,
  });

  const prevDocType = useRef(documentType);
  const prevInvoiceTypeCode = useRef(invoiceTypeCode);

  useEffect(() => {
    if (prevDocType.current !== documentType) {
      setValue('accounting_supplier_party', null);
      setValue('accounting_customer_party', null);

      populated.current.billedBy = false;
      populated.current.billedTo = false;

      prevDocType.current = documentType;
      return;
    }

    const resolvedDocumentTypeCode =
      invoiceTypeCode ?? getValues('invoice_type_code') ?? getValues('credit_note_type_code');

    if (prevInvoiceTypeCode.current !== resolvedDocumentTypeCode) {
      populated.current.billedBy = false;
      prevInvoiceTypeCode.current = resolvedDocumentTypeCode;
    }

    if (documentType === 'SALES') {
      if (
        !populated.current.billedBy &&
        Array.isArray(profiles) &&
        profiles.length === 1 &&
        isEligibleBilledByProfile(profiles[0], resolvedDocumentTypeCode)
      ) {
        const existing = getValues('accounting_supplier_party');
        if (!existing) {
          setValue('accounting_supplier_party', profiles[0], {
            shouldValidate: true,
            shouldDirty: false,
          });
          populated.current.billedBy = true;
        }
      }

      if (!populated.current.billedTo && Array.isArray(customers) && customers.length === 1) {
        const existing = getValues('accounting_customer_party');
        if (!existing) {
          setValue('accounting_customer_party', customers[0], {
            shouldValidate: true,
            shouldDirty: false,
          });
          populated.current.billedTo = true;
        }
      }
    }

    if (documentType === 'PURCHASE') {
      if (
        !populated.current.billedBy &&
        Array.isArray(suppliers) &&
        suppliers.length === 1 &&
        hasVatNumber(suppliers[0])
      ) {
        const existing = getValues('accounting_supplier_party');
        if (!existing) {
          setValue('accounting_supplier_party', suppliers[0], {
            shouldValidate: true,
            shouldDirty: false,
          });
          populated.current.billedBy = true;
        }
      }

      if (
        !populated.current.billedTo &&
        Array.isArray(profiles) &&
        profiles.length === 1 &&
        isBusinessProfileComplete(profiles[0])
      ) {
        const existing = getValues('accounting_customer_party');
        if (!existing) {
          setValue('accounting_customer_party', profiles[0], {
            shouldValidate: true,
            shouldDirty: false,
          });
          populated.current.billedTo = true;
        }
      }
    }
  }, [profiles, customers, suppliers, documentType, invoiceTypeCode, setValue, getValues]);
}
